import React from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  useTheme,
  useMediaQuery,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { DropDownArrowIcon } from "@ippon/shared-assets";
import { RequireIcon as CustomRequireIcon } from "@ippon/shared-assets";
import "./CustomMultiSelect.css";
import { Controller } from "react-hook-form";
import { useResponsiveMenuPlacement } from "../../../hooks/useResponsiveMenuPlacement";

export interface CustomMultiSelectProps {
  label: string;
  name: string;
  placeholder?: string;
  control?: any;
  errors?: any;
  disabled?: boolean;
  readonly?: boolean;
  isRequired?: boolean;
  extraClassName?: string;
  isShowInColumn?: boolean;
  options?: Array<{ value: string | number; label: string }>;
  labelWidth?: string;
  labelWidthSp?: string;
  inputWidth?: string;
  inputWidthSp?: string;
  onChangeValue?: (val: any) => void;
  sx?: any;
  hasSpacing?: boolean;
  /**
   * 選択済みサマリーの表示文言をカスタマイズする (省略時は `${count}件選択`)。
   * 例) 日付セレクトなら `(n) => `${n}日選択中``。
   */
  selectedSummary?: (count: number) => string;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const DEFAULT_MENU_MAX_HEIGHT = ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP;

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  label,
  name,
  placeholder = "",
  control,
  errors = {},
  disabled = false,
  readonly = false,
  isRequired = false,
  extraClassName = "flexRow",
  isShowInColumn = false,
  options = [],
  labelWidth = "98px",
  labelWidthSp = "100px",
  inputWidth = "100%",
  inputWidthSp = "100%",
  onChangeValue,
  hasSpacing,
  selectedSummary,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    menuMaxHeight,
    menuAnchorOrigin,
    menuTransformOrigin,
    updateMenuPlacement,
  } = useResponsiveMenuPlacement(DEFAULT_MENU_MAX_HEIGHT);
  const [menuMinWidth, setMenuMinWidth] = React.useState<number>(0);

  const handleOpen = React.useCallback(
    (event: React.SyntheticEvent) => {
      const selectElement = event.currentTarget as HTMLElement;
      setMenuMinWidth(selectElement.getBoundingClientRect().width);
      updateMenuPlacement(selectElement);
    },
    [updateMenuPlacement],
  );

  const renderValue = (selected: any) => {
    if (!options || options.length === 0) {
      return (
        <span style={{ color: "#989898", fontSize: "12px" }}>
          {placeholder || "選択してください"}
        </span>
      );
    }

    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return (
        <span style={{ color: "#989898", fontSize: "12px" }}>
          {placeholder || "選択してください"}
        </span>
      );
    }

    if (Array.isArray(selected)) {
      const selectedCount = selected.length;
      if (selectedCount === 0) {
        return (
          <span style={{ color: "#989898", fontSize: "12px" }}>
            {placeholder || "選択してください"}
          </span>
        );
      }
      return (
        <span style={{ color: "#3E3E3E" }}>
          {selectedSummary
            ? selectedSummary(selectedCount)
            : `${selectedCount}件選択`}
        </span>
      );
    }

    return "";
  };

  return (
    <Box
      className={`inputGroupWrapper ${
        isShowInColumn ? "isShowInColumn" : ""
      } ${extraClassName} ${isMobile ? "sp" : ""} ${
        hasSpacing === false ? "noSpacing" : ""
      }`}
      // AI 画面コンテキスト用 (getPageContext が拾う)
      data-field={name || undefined}
      data-label={label || undefined}
      data-options={
        options && options.length > 0 ? JSON.stringify(options) : undefined
      }
      sx={{ display: "flex !important", width: { xs: "100%", sm: "inherit" } }}
    >
      <Typography
        className="inputLabel"
        sx={{
          width: { sm: labelWidth, xs: labelWidthSp ?? "47%" },
          whiteSpace: "nowrap",
          fontSize: "12px !important",
          marginBottom: "1px",
        }}
        paddingBottom={
          errors[name] && extraClassName !== "flexCol"
            ? "24px"
            : extraClassName === "flexCol"
              ? "10px"
              : ""
        }
      >
        {label}
        {isRequired && (
          <span style={{ display: "flex", marginLeft: "4px" }}>
            <CustomRequireIcon />
          </span>
        )}
      </Typography>
      <Box
        sx={{
          width: { sm: inputWidth, xs: inputWidthSp },
        }}
      >
        <FormControl
          fullWidth
          error={isRequired ? !!errors[name] : false}
          disabled={disabled}
        >
          <Controller
            name={name}
            control={control}
            rules={isRequired ? { required: `${label}は必須です` } : {}}
            render={({ field }) => (
              <Select
                {...field}
                multiple
                displayEmpty
                value={field.value || []}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  if (onChangeValue) onChangeValue(e.target.value);
                }}
                input={
                  <OutlinedInput
                    sx={{
                      fontSize: "12px",
                      "& .MuiOutlinedInput-input": {
                        fontSize: "12px",
                        color: "#989898",
                      },
                    }}
                  />
                }
                renderValue={renderValue}
                IconComponent={DropDownArrowIcon}
                readOnly={readonly}
                onOpen={handleOpen}
                MenuProps={{
                  anchorOrigin: menuAnchorOrigin,
                  transformOrigin: menuTransformOrigin,
                  PaperProps: {
                    style: {
                      maxHeight: menuMaxHeight,
                      width: "max-content",
                      minWidth: menuMinWidth,
                      maxWidth: "calc(100vw - 32px)",
                    },
                  },
                }}
                sx={{
                  height: { sm: "34px", xs: "32px" },
                  mb: 1,
                  fontSize: "12px",
                  backgroundColor: readonly ? "#F5F5F5" : "#fff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: errors[name] ? "#d32f2f" : "#D9D9D9",
                    borderRadius: "8px",
                    borderWidth: "1px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: errors[name] ? "#d32f2f" : "#D9D9D9",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: errors[name] ? "#d32f2f" : "#D9D9D9",
                    borderWidth: "1px",
                  },
                  // 矢印アイコンを縦中央に配置 (DropDownArrowIcon は 14×9 の小さい SVG のため)
                  "& .MuiSelect-icon": {
                    top: "50%",
                    transform: "translateY(-50%)",
                    right: 10,
                  },
                  "&:hover": {
                    backgroundColor: readonly ? "#F5F5F5" : "#fff",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#F5F5F5 !important",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                    },
                  },
                  ".MuiSelect-icon": {
                    color: "#666",
                    fontSize: "14px",
                    display: readonly ? "none" : disabled ? "none" : "block",
                  },
                  "& .MuiSelect-select": {
                    color:
                      field.value &&
                      Array.isArray(field.value) &&
                      field.value.length > 0
                        ? readonly || disabled
                          ? "#3e3e3e"
                          : "inherit"
                        : "#989898",
                    padding: "8px 22px 8px 12px !important",
                    display: "flex",
                    alignItems: "center",
                    minHeight: "18px",
                    fontSize: "12px",
                    "& span": {
                      fontSize: "12px",
                    },
                  },
                  "& .MuiSelect-select.Mui-disabled": {
                    color: "#3e3e3e !important",
                    WebkitTextFillColor: "#3e3e3e !important",
                  },
                  "& .MuiMenuItem-root": {
                    "&.Mui-selected": {
                      backgroundColor: "rgba(11, 157, 189, 0.08)",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "rgba(11, 157, 189, 0.12)",
                    },
                  },
                }}
              >
                <MenuItem
                  disabled
                  sx={{
                    fontSize: "12px",
                    color: "#989898 !important",
                  }}
                >
                  {placeholder || "選択してください"}
                </MenuItem>
                {options.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      fontSize: "12px",
                      height: "32px",
                      padding: "4px 12px",
                      minWidth: "max-content",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Checkbox
                      checked={
                        field.value &&
                        Array.isArray(field.value) &&
                        field.value.indexOf(option.value) > -1
                      }
                      sx={{
                        padding: "0",
                        marginRight: "8px",
                        "&.Mui-checked": {
                          color: "#0B9DBD",
                        },
                      }}
                    />
                    <ListItemText
                      primary={option.label}
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontSize: "12px",
                          color: "#3E3E3E",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors[name] && (
            <FormHelperText
              sx={{
                color: "#d32f2f",
                fontSize: "12px",
                margin: "4px 0 0 0",
              }}
            >
              {errors[name].message}
            </FormHelperText>
          )}
        </FormControl>
      </Box>
    </Box>
  );
};

export default CustomMultiSelect;
